import { Box, Heading, SimpleGrid, Spinner, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import {
  getHomeEmbodiedCarbon,
  getHomeOperationalCarbon,
} from "../../services/portfolioApi"
import { formatCo2Kg } from "../../utils/carbonUtils"
import SummaryStatCard from "./SummaryStatCard"

const fmtCo2 = (val: number | null | undefined) =>
  val != null ? formatCo2Kg(val) : "–"

interface CarbonTabProps {
  homeId: number
  isActive: boolean
}

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Heading size="lg" mb={4}>{children}</Heading>
)

const NotAvailable = ({ message }: { message: string }) => (
  <Box bg="gray.50" borderRadius="lg" p={6} textAlign="center">
    <Text color="gray.500" fontSize="sm">{message}</Text>
  </Box>
)

const CarbonTab = ({ homeId, isActive }: CarbonTabProps) => {
  const {
    data: operational,
    isLoading: opLoading,
    isError: opError,
  } = useQuery({
    queryKey: ["operationalCarbon", homeId],
    queryFn: () => getHomeOperationalCarbon(homeId),
    retry: false,
    enabled: isActive,
  })

  const {
    data: embodied,
    isLoading: embLoading,
    isError: embError,
  } = useQuery({
    queryKey: ["embodiedCarbon", homeId],
    queryFn: () => getHomeEmbodiedCarbon(homeId),
    retry: false,
    enabled: isActive,
  })

  const isLoading = opLoading || embLoading

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4} color="gray.500">Loading carbon data...</Text>
      </Box>
    )
  }

  const preTotal = operational?.totalCo2KgPre ?? null
  const postTotal = operational?.totalCo2KgPost ?? null
  const savings = operational?.co2SavingsKg ?? null
  const reductionPct = operational?.co2ReductionPct ?? null

  const totalEmbodied = embodied?.totalEmbodiedCarbonKgco2e ?? null
  const paybackYears = embodied?.carbonPaybackYears ?? null
  const netBenefit = embodied?.netCarbonBenefitKgco2e ?? null
  const annualSaving = embodied?.annualOperationalCarbonSavingKgco2e ?? null

  return (
    <VStack align="stretch" gap={8} pt={6}>
      <SimpleGrid columns={3} gap={4}>
        <SummaryStatCard
          title="Annual CO₂ reduction"
          value={fmtCo2(savings)}
          subline="Avoided emissions per year post-retrofit"
          borderColor="gray.100"
        />
        <SummaryStatCard
          title="Reduction vs baseline"
          value={reductionPct != null ? `${reductionPct}%` : "–"}
          subline="Post-upgrade vs pre-upgrade emissions"
          borderColor="gray.100"
        />
        <SummaryStatCard
          title="Carbon payback"
          value={paybackYears != null ? `${Number(paybackYears).toLocaleString("en-GB", { maximumFractionDigits: 1 })} yrs` : "–"}
          subline="Years until embodied carbon is offset"
          borderColor="gray.100"
        />
      </SimpleGrid>

      {/* Operational Carbon */}
      <Box>
        <SectionHeading>Operational Carbon: Scope 1 &amp; 2</SectionHeading>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Annual CO₂e from direct fuel combustion (Scope 1) and purchased electricity (Scope 2), pre- and post-retrofit.
          Based on {operational?.emissionFactorYear ?? "current"} DESNZ grid emission factors.
        </Text>

        {opError || !operational ? (
          <NotAvailable message="Operational carbon analysis not yet available for this home. Run an energy analysis first." />
        ) : (
          <VStack align="stretch" gap={4}>
            <SimpleGrid columns={4} gap={4}>
              <SummaryStatCard
                title="Pre-upgrade emissions"
                value={fmtCo2(preTotal)}
                subline="Annual kgCO₂e, Scope 1 + 2"
                borderColor="gray.100"
              />
              <SummaryStatCard
                title="Post-upgrade emissions"
                value={fmtCo2(postTotal)}
                subline="Annual kgCO₂e, Scope 2 only"
                borderColor="gray.100"
              />
              <SummaryStatCard
                title="Annual CO₂ reduction"
                value={fmtCo2(savings)}
                subline="Avoided Scope 1 + 2 per year"
                borderColor="gray.100"
              />
              <SummaryStatCard
                title="Reduction vs baseline"
                value={reductionPct != null ? `${reductionPct}%` : "–"}
                subline="Post-upgrade vs pre-upgrade"
                borderColor="gray.100"
              />
            </SimpleGrid>

            <SimpleGrid columns={2} gap={4}>
              <Box bg="gray.50" borderRadius="lg" p={5} border="1px solid" borderColor="gray.100">
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={3}>
                  Pre-upgrade breakdown
                </Text>
                <VStack align="stretch" gap={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Text fontSize="sm" color="gray.500">Scope 1: Gas (space heating + hot water)</Text>
                    <Text fontSize="sm" fontWeight="medium">{fmtCo2(operational.gasCo2KgPre)}</Text>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Text fontSize="sm" color="gray.500">Scope 2: Purchased electricity</Text>
                    <Text fontSize="sm" fontWeight="medium">{fmtCo2(operational.electricityCo2KgPre)}</Text>
                  </Box>
                </VStack>
              </Box>
              <Box bg="gray.50" borderRadius="lg" p={5} border="1px solid" borderColor="gray.100">
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={3}>
                  Post-upgrade breakdown
                </Text>
                <VStack align="stretch" gap={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Text fontSize="sm" color="gray.500">Scope 1: Gas (eliminated)</Text>
                    <Text fontSize="sm" fontWeight="medium">0 kg CO₂e</Text>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Text fontSize="sm" color="gray.500">Scope 2: Electricity (net of solar)</Text>
                    <Text fontSize="sm" fontWeight="medium">{fmtCo2(operational.electricityCo2KgPost)}</Text>
                  </Box>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        )}
      </Box>

      {/* Embodied Carbon */}
      <Box>
        <SectionHeading>Embodied Carbon: Scope 3 (upstream)</SectionHeading>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Lifecycle CO₂e from manufacturing the retrofit measures (heat pump, solar PV, battery). Scope 3 upstream emissions per GHG Protocol.
          Offset period shows how long operational savings take to recover this upfront carbon cost.
          Broader Scope 3 categories (tenant behaviour, maintenance supply chain) are outside the boundary of this screening-level analysis.
        </Text>

        {embError || !embodied ? (
          <NotAvailable message="Embodied carbon data not yet available. Retrofit upgrades must be calculated first." />
        ) : (
          <VStack align="stretch" gap={4}>
            <SimpleGrid columns={4} gap={4}>
              <SummaryStatCard
                title="Total embodied carbon"
                value={fmtCo2(totalEmbodied)}
                subline="Upfront CO₂e from all retrofit measures"
                borderColor="gray.100"
              />
              <SummaryStatCard
                title="Annual operational saving"
                value={fmtCo2(annualSaving)}
                subline="CO₂e avoided per year post-retrofit"
                borderColor="gray.100"
              />
              <SummaryStatCard
                title="Carbon payback"
                value={paybackYears != null ? `${Number(paybackYears).toLocaleString("en-GB", { maximumFractionDigits: 1 })} yrs` : "–"}
                subline="Years to offset embodied carbon"
                borderColor="gray.100"
              />
              <SummaryStatCard
                title="Net benefit (25 yrs)"
                value={fmtCo2(netBenefit)}
                subline="Total CO₂e saved over analysis period"
                borderColor="gray.100"
              />
            </SimpleGrid>

            {embodied.items.length > 0 && (
              <Box bg="gray.50" borderRadius="lg" p={5} border="1px solid" borderColor="gray.100">
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={3}>
                  Embodied carbon by measure
                </Text>
                <VStack align="stretch" gap={2}>
                  {embodied.items.map((item) => (
                    <Box key={item.technology} display="flex" justifyContent="space-between" alignItems="center">
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" textTransform="capitalize">
                          {item.technology.replace("_", " ")}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {item.expectedLifeYears} yr lifespan · {item.confidence} confidence
                        </Text>
                      </VStack>
                      <VStack align="end" gap={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {fmtCo2(item.totalEmbodiedCarbonKgco2e)}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {fmtCo2(item.annualisedEmbodiedCarbonKgco2e)}/yr
                        </Text>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </Box>
    </VStack>
  )
}

export default CarbonTab
